import type { ValidComponent } from 'solid-js'
import { createMemo, useContext } from 'solid-js'
import { Dynamic } from 'solid-js/web'
import { twMerge } from 'tailwind-merge'
import type { ListItemPropsType } from '~/types/ListItem'
import { ListContext } from './List'

export default function ListItem(props: ListItemPropsType) {
  const listContext = useContext(ListContext)
  const Component = props.button ? 'button' : props.component || 'li'

  const itemClasses = createMemo(() => {
    const selectedVal = typeof props.selected === 'function' ? props.selected() : props.selected
    const color: string = props.selectedColor ?? 'primary'
    const hasSelectedColor = props.selectedColor !== undefined

    const SELECTED_COLOR_CLASSES: Record<string, string> = {
      danger: 'bg-danger-soft',
      primary: 'bg-primary-soft',
      secondary: 'bg-inverse/10',
      success: 'bg-success-soft',
      warning: 'bg-warning-soft',
      info: 'bg-info-soft',
      default: 'bg-inverse/10'
    }

    const SUBTLE_SELECTED = 'bg-surface-muted'

    const HOVER_COLOR_CLASSES: Record<string, string> = {
      danger: 'hover:bg-danger-soft',
      primary: 'hover:bg-primary-soft',
      secondary: 'hover:bg-surface-hover',
      success: 'hover:bg-success-soft',
      warning: 'hover:bg-warning-soft',
      info: 'hover:bg-info-soft',
      default: 'hover:bg-surface-hover'
    }

    const DEFAULT_HOVER = 'hover:bg-surface-hover'

    const FOCUS_COLOR_CLASSES: Record<string, string> = {
      danger: 'focus:bg-danger-soft',
      primary: 'focus:bg-primary-soft',
      secondary: 'focus:bg-surface-hover',
      success: 'focus:bg-success-soft',
      warning: 'focus:bg-warning-soft',
      info: 'focus:bg-info-soft',
      default: 'focus:bg-surface-hover'
    }

    const DEFAULT_FOCUS = 'focus:bg-surface-hover'

    // Background when selected: use selectedColor only if provided, otherwise use a subtle tone
    const selectedBg = selectedVal ? (hasSelectedColor ? SELECTED_COLOR_CLASSES[color] || SUBTLE_SELECTED : SUBTLE_SELECTED) : ''

    // Hover/focus classes: use color-specific only if selectedColor is provided, otherwise use subtle defaults
    const hoverClass = hasSelectedColor ? HOVER_COLOR_CLASSES[color] || DEFAULT_HOVER : DEFAULT_HOVER
    const focusClass = hasSelectedColor ? FOCUS_COLOR_CLASSES[color] || DEFAULT_FOCUS : DEFAULT_FOCUS

    return twMerge(
      'flex w-full min-h-12 text-left items-center',
      !listContext.disablePadding && 'px-4 py-2',
      props.divider && 'border-b border-border',
      props.disabled && 'opacity-50 cursor-not-allowed pointer-events-none',
      selectedBg,
      props.button && !props.disabled && ['cursor-pointer', hoverClass, focusClass, 'focus:outline-none', 'transition-colors duration-200 ease-in-out', 'rounded-md'],
      props.class
    )
  })

  return (
    <Dynamic
      component={Component as ValidComponent}
      class={itemClasses()}
      classList={props.classList}
      onClick={!props.disabled ? props.onClick : undefined}
      disabled={props.disabled}
      role={props.button ? 'button' : 'listitem'}
      tabIndex={props.button && !props.disabled ? 0 : undefined}
    >
      {props.children}
    </Dynamic>
  )
}
